import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { buildAuthHeaders } from '@/lib/authStorage';
import { toast } from 'sonner';
import { CatalogContractBadges } from '@/components/template-builder/CatalogContractBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY_FORM = {
  id: null,
  path: '',
  label: '',
  module: 'GLOBAL',
  doc_types: '*',
  group: 'Catálogo ERP',
  kind: 'variable',
};

export const ErpCatalogPanel = ({ title = 'Catálogo ERP', description = 'Campos e datasources disponíveis para templates.', testIdPrefix = 'erp-catalog-external', editable = false }) => {
  const [catalog, setCatalog] = useState(null);
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState('__all__');
  const [docTypeFilter, setDocTypeFilter] = useState('__all__');
  const [formState, setFormState] = useState(EMPTY_FORM);

  const loadCatalog = async () => {
    try {
      const response = await fetch(`${API_URL}/api/templates/erp-elements-catalog`, {
        headers: buildAuthHeaders(),
      });
      if (!response.ok) return;
      const payload = await response.json();
      setCatalog(payload);
    } catch {
      // silêncio: painel continua vazio se catálogo falhar
    }
  };

  useEffect(() => {
    let mounted = true;
    const safeLoad = async () => {
      try {
        const response = await fetch(`${API_URL}/api/templates/erp-elements-catalog`, {
          headers: buildAuthHeaders(),
        });
        if (!response.ok) return;
        const payload = await response.json();
        if (mounted) setCatalog(payload);
      } catch {
        // silêncio: painel continua vazio se catálogo falhar
      }
    };
    safeLoad();
    return () => {
      mounted = false;
    };
  }, []);

  const modules = useMemo(() => catalog?.modules || [], [catalog]);
  const docTypes = useMemo(() => catalog?.doc_types || [], [catalog]);
  const items = useMemo(() => catalog?.flat_items || [], [catalog]);
  const contractSummary = useMemo(() => catalog?.contract_summary || {}, [catalog]);
  const catalogSourceLabel = catalog?.catalog_source === 'canonical_backend' ? 'Contrato backend' : 'Fallback local';

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter((item) => {
      const haystack = [item.path, item.label, item.group, item.module, ...(item.doc_types || [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      const matchesSearch = !query || haystack.includes(query);
      const matchesModule = moduleFilter === '__all__' || item.module === moduleFilter;
      const matchesDocType = docTypeFilter === '__all__' || (item.doc_types || []).includes(docTypeFilter) || (item.doc_types || []).includes('*');
      return matchesSearch && matchesModule && matchesDocType;
    });
  }, [docTypeFilter, items, moduleFilter, search]);

  const handleEdit = (item) => {
    setFormState({
      id: item.id || null,
      path: item.path || '',
      label: item.label || '',
      module: item.module || 'GLOBAL',
      doc_types: (item.doc_types || []).join(', ') || '*',
      group: item.group || 'Catálogo ERP',
      kind: item.kind || 'variable',
    });
  };

  const handleSave = async () => {
    try {
      const payload = {
        path: formState.path.trim(),
        label: formState.label.trim(),
        module: formState.module.trim() || 'GLOBAL',
        doc_types: formState.doc_types.split(',').map((item) => item.trim()).filter(Boolean),
        group: formState.group.trim() || 'Catálogo ERP',
        kind: formState.kind,
      };
      const targetUrl = formState.id
        ? `${API_URL}/api/templates/erp-elements-catalog/entries/${formState.id}`
        : `${API_URL}/api/templates/erp-elements-catalog/entries`;
      const response = await fetch(targetUrl, {
        method: formState.id ? 'PUT' : 'POST',
        headers: {
          ...buildAuthHeaders(),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });
      if (!response.ok) throw new Error('Falha ao guardar entrada do catálogo');
      toast.success(formState.id ? 'Entrada actualizada com sucesso.' : 'Entrada criada com sucesso.');
      setFormState(EMPTY_FORM);
      await loadCatalog();
    } catch (error) {
      toast.error(error.message || 'Não foi possível guardar a entrada.');
    }
  };

  const handleDelete = async (itemId) => {
    try {
      const response = await fetch(`${API_URL}/api/templates/erp-elements-catalog/entries/${itemId}`, {
        method: 'DELETE',
        headers: buildAuthHeaders(),
      });
      if (!response.ok) throw new Error('Falha ao remover entrada do catálogo');
      toast.success('Entrada removida com sucesso.');
      if (formState.id === itemId) setFormState(EMPTY_FORM);
      await loadCatalog();
    } catch (error) {
      toast.error(error.message || 'Não foi possível remover a entrada.');
    }
  };

  return (
    <div className="space-y-1.5" data-testid={`${testIdPrefix}-panel`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-sm font-semibold" data-testid={`${testIdPrefix}-title`}>{title}</h3>
        <Badge variant="outline" data-testid={`${testIdPrefix}-catalog-source-badge`}>{catalogSourceLabel}</Badge>
        <p className="w-full text-xs text-muted-foreground" data-testid={`${testIdPrefix}-description`}>{description}</p>
      </div>

      {editable && (
        <div className="grid gap-1.5 rounded-lg border p-2 md:grid-cols-2" data-testid={`${testIdPrefix}-editor-form`}>
          <Input value={formState.path} onChange={(event) => setFormState((prev) => ({ ...prev, path: event.target.value }))} placeholder="Path" data-testid={`${testIdPrefix}-form-path`} />
          <Input value={formState.label} onChange={(event) => setFormState((prev) => ({ ...prev, label: event.target.value }))} placeholder="Etiqueta" data-testid={`${testIdPrefix}-form-label`} />
          <Input value={formState.module} onChange={(event) => setFormState((prev) => ({ ...prev, module: event.target.value }))} placeholder="Módulo" data-testid={`${testIdPrefix}-form-module`} />
          <Input value={formState.group} onChange={(event) => setFormState((prev) => ({ ...prev, group: event.target.value }))} placeholder="Grupo" data-testid={`${testIdPrefix}-form-group`} />
          <Input value={formState.doc_types} onChange={(event) => setFormState((prev) => ({ ...prev, doc_types: event.target.value }))} placeholder="doc_types separados por vírgula" data-testid={`${testIdPrefix}-form-doc-types`} />
          <Select value={formState.kind} onValueChange={(value) => setFormState((prev) => ({ ...prev, kind: value }))}>
            <SelectTrigger data-testid={`${testIdPrefix}-form-kind`}><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="variable">Campo</SelectItem>
              <SelectItem value="datasource">Datasource</SelectItem>
            </SelectContent>
          </Select>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <Button size="sm" onClick={handleSave} data-testid={`${testIdPrefix}-save-button`}>{formState.id ? 'Actualizar entrada' : 'Adicionar entrada'}</Button>
            <Button size="sm" variant="outline" onClick={() => setFormState(EMPTY_FORM)} data-testid={`${testIdPrefix}-reset-button`}>Limpar</Button>
          </div>
        </div>
      )}

      <div className="grid gap-1.5 md:grid-cols-[minmax(0,1.2fr)_150px_150px]" data-testid={`${testIdPrefix}-filters`}>
        <Input
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Pesquisar por path, etiqueta, módulo ou doc_type"
          className="h-8 text-xs"
          data-testid={`${testIdPrefix}-search-input`}
        />
        <Select value={moduleFilter} onValueChange={setModuleFilter}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-module-filter`}>
            <SelectValue placeholder="Todos os módulos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os módulos</SelectItem>
            {modules.map((moduleOption) => (
              <SelectItem key={`${testIdPrefix}-module-${moduleOption}`} value={moduleOption}>{moduleOption}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={docTypeFilter} onValueChange={setDocTypeFilter}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${testIdPrefix}-doc-type-filter`}>
            <SelectValue placeholder="Todos os doc_types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Todos os doc_types</SelectItem>
            {docTypes.map((docTypeOption) => (
              <SelectItem key={`${testIdPrefix}-doc-type-${docTypeOption}`} value={docTypeOption}>{docTypeOption}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-1.5 md:grid-cols-5" data-testid={`${testIdPrefix}-summary-grid`}>
        {[
          { key: 'total', label: 'Contrato total', value: contractSummary.total_items || 0 },
          { key: 'derived', label: 'Derivados', value: contractSummary.derived_items || 0 },
          { key: 'legacy', label: 'Legacy', value: contractSummary.legacy_items || 0 },
          { key: 'custom', label: 'Custom', value: contractSummary.custom_items || 0 },
          { key: 'runtime', label: 'Runtime only', value: contractSummary.runtime_only_items || 0 },
        ].map((item) => (
          <div key={item.key} className="rounded-lg border bg-muted/30 px-2 py-1.5" data-testid={`${testIdPrefix}-summary-${item.key}`}>
            <p className="text-[9px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</p>
            <p className="mt-1 text-sm font-semibold">{item.value}</p>
          </div>
        ))}
      </div>

      <div className="divide-y rounded-lg border" data-testid={`${testIdPrefix}-list`}>
          {filteredItems.map((item) => (
            <div key={`${testIdPrefix}-${item.kind}-${item.path}`} className="px-2 py-1.5" data-testid={`${testIdPrefix}-item-${item.path.replace(/[^a-zA-Z0-9]+/g, '-')}`}>
              <div className="flex flex-wrap items-start justify-between gap-1">
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-semibold leading-tight">{item.label || item.path}</p>
                  <p className="mt-0.5 break-all font-mono text-[9px] text-slate-700">{item.path}</p>
                </div>
                <div className="flex flex-wrap gap-1">
                  <CatalogContractBadges item={item} testIdBase={`${testIdPrefix}-item-meta-${item.path.replace(/[^a-zA-Z0-9]+/g, '-')}`} />
                  {editable && item.is_custom && (
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px]" onClick={() => handleEdit(item)} data-testid={`${testIdPrefix}-edit-${item.id}`}>Editar</Button>
                      <Button size="sm" variant="outline" className="h-5 px-1.5 text-[9px]" onClick={() => handleDelete(item.id)} data-testid={`${testIdPrefix}-delete-${item.id}`}>Apagar</Button>
                    </div>
                  )}
                </div>
              </div>
              <p className="mt-0.5 text-[9px] text-muted-foreground">Grupo: {item.group || 'Catálogo ERP'} · Família: {item.document_family || '*'} · Doc types: {(item.doc_types || []).join(', ') || '*'}</p>
            </div>
          ))}
          {filteredItems.length === 0 && (
            <div className="rounded-lg border border-dashed p-2 text-[11px] text-muted-foreground" data-testid={`${testIdPrefix}-empty-state`}>
              Nenhum elemento encontrado para os filtros actuais.
            </div>
          )}
      </div>
    </div>
  );
};